import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@shared/components/Button';
import Layout from '@shared/components/Layout';
import { publishButtonClickedEvent } from '@shared/utils/analytics/events';
import {
  getMfaScreenLayoutProps,
  MfaScreenLayoutProps,
} from '../../mfa/layout';

export interface TrustDeviceScreenProps extends MfaScreenLayoutProps {
  onClickNo: () => Promise<void>;
  onClickYes: () => Promise<void>;
  errorText?: string;
  resetError: () => void;
}

type PendingAction = 'no' | 'yes';

const TrustDeviceScreen: React.FC<TrustDeviceScreenProps> = ({
  onClickNo,
  onClickYes,
  errorText,
  resetError,
  variant,
  bannerImageDesktop,
  hideLogo,
  headerClasses,
}) => {
  const { t } = useTranslation();
  const [pendingAction, setPendingAction] = useState<PendingAction>();
  const [submitError, setSubmitError] = useState<string>();
  const layoutProps = getMfaScreenLayoutProps({
    variant,
    bannerImageDesktop,
    hideLogo,
    headerClasses,
  });

  useEffect(() => {
    if (!errorText) {
      return;
    }

    setPendingAction(undefined);
    setSubmitError(undefined);
  }, [errorText]);

  const handleClick = async (
    action: PendingAction,
    callback: () => Promise<void>
  ) => {
    publishButtonClickedEvent(
      action === 'yes' ? 'Trust Device Yes' : 'Trust Device No',
      'Log In - Enter MFA Code'
    );
    resetError();
    setSubmitError(undefined);
    setPendingAction(action);

    try {
      await callback();
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : t('Unable to update trusted device.')
      );
      setPendingAction(undefined);
    }
  };

  const renderedErrorText = errorText || submitError;
  const isSubmitting = Boolean(pendingAction);

  return (
    <Layout
      heading={t('New Device')}
      subHeading={t('Mark this device as trusted?')}
      {...layoutProps}
    >
      <div className="mx-auto w-full max-w-[320px] space-y-6">
        {renderedErrorText && (
          <p className="text-red-600 text-center text-sm">
            {renderedErrorText}
          </p>
        )}

        <div className="flex justify-center gap-4">
          <Button
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
              void handleClick('no', onClickNo);
            }}
            disabled={isSubmitting && pendingAction !== 'no'}
            label={t('No')}
            state={pendingAction === 'no' ? 'loading' : 'default'}
            fullWidth={false}
            className="min-w-[120px]"
          />
          <Button
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
              void handleClick('yes', onClickYes);
            }}
            variant="primary"
            disabled={isSubmitting && pendingAction !== 'yes'}
            label={t('Yes')}
            state={pendingAction === 'yes' ? 'loading' : 'default'}
            fullWidth={false}
            className="min-w-[120px]"
          />
        </div>
      </div>
    </Layout>
  );
};

export default TrustDeviceScreen;
